import {MeteringRecord} from '../types';
import {I18n} from '@aws-amplify/core';
import {
    PlatformPropertyCard,
    PlatformPropertyCardCopyButtonDecorator,
    PlatformPropertyCardEntryInput,
} from "@flexinet/ui-components";
import {Code, Stack, Text, Title} from "@mantine/core";
import {StatusBadge} from "./StatusBadge.tsx";
import {prettyPrintAwsJson} from "../utils/parseAwsJson.ts";

const JsonBlock = ({title, value}: { title: string, value: string }) => (
    <Stack gap={'xs'}>
        <Title order={5}>{title}</Title>
        {value
            ? <Code block style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{value}</Code>
            : <Text c={'dimmed'} size={'sm'}>—</Text>}
    </Stack>
);

// ⚠️ Typed PlatformPropertyCardEntryInput[], never cast (FNP-1890, as FNP-1882). The
// arrays used to end in `as PlatformPropertyCardProperty[]`, which switched off the
// excess-property check: `formatAs: 'Timestamp'` compiled clean, the card ignored it,
// and every timestamp rendered as raw ISO. Typed, a wrong key is a tsc error.
export const Profile = ({record}: { record: MeteringRecord }) => {

    const identityProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: I18n.get('Metering Record ID'),
            value: record.meteringRecordId,
            formatter: 'code',
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: I18n.get('State'),
            value: <StatusBadge status={record.state}/>,
            span: 12,
        },
        {
            label: I18n.get('Customer ID'),
            value: record.customerId,
            formatter: 'code',
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: I18n.get('Meter ID'),
            value: record.meterId,
            formatter: 'code',
            span: 12,
        },
        {
            label: I18n.get('Meter Code'),
            value: record.meterCode,
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Sort Key'),
            value: record.sortKey,
            formatter: 'code',
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: I18n.get('Dimensions Hash'),
            value: record.dimensionsHash,
            formatter: 'code',
            span: 12,
        },
        {
            label: I18n.get('Record Type'),
            value: record.recordType,
            span: 12,
        },
    ];

    const aggregationProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: I18n.get('Value'),
            value: record.value,
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Input Event Count'),
            value: record.inputEventCount,
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Period Start'),
            value: record.periodStart,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Period End'),
            value: record.periodEnd,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Watermark'),
            value: record.watermark,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Computed At'),
            value: record.computedAt,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Created'),
            value: record.created,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 12,
        },
        {
            label: I18n.get('Last Updated'),
            value: record.updated,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 12,
        },
    ];

    return (
        <Stack gap={'lg'}>
            <PlatformPropertyCard properties={identityProperties}/>
            <PlatformPropertyCard properties={aggregationProperties}/>

            {/* dimensions is the AWSJSON breakdown the aggregate is keyed on. */}
            <JsonBlock title={I18n.get('Dimensions')} value={prettyPrintAwsJson(record.dimensions)}/>
        </Stack>
    );
};
